import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  DataGrid,
  GridColDef,
  GridActionsCellItem,
} from "@mui/x-data-grid";
import {
  collection,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  getDoc,
  query,
  orderBy,
  serverTimestamp,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import { db, auth } from "../firebase";

interface ListItem {
  id: string;
  value: string;
}

type Role = "owner" | "editor" | "viewer" | "admin";

export default function ListEditor() {
  const { id: listId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [listName, setListName] = useState<string>("");
  const [items, setItems] = useState<ListItem[]>([]);
  const [newValue, setNewValue] = useState("");

  const [editingName, setEditingName] = useState(false);
  const [editedName, setEditedName] = useState("");

  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState("");

  const [editOpen, setEditOpen] = useState(false);
  const [editValue, setEditValue] = useState("");
  const [editItemId, setEditItemId] = useState<string | null>(null);

  const [splitOpen, setSplitOpen] = useState(false);
  const [splitItemId, setSplitItemId] = useState<string | null>(null);
  const [splitOriginalValue, setSplitOriginalValue] = useState("");
  const [splitParts, setSplitParts] = useState<string[]>([]);
  const [splitError, setSplitError] = useState<string | null>(null);

  const [role, setRole] = useState<Role | null>(null);
  const [errMsg, setErrMsg] = useState<string | null>(null);

  const readOnly = role === "viewer";

  // --- Fetch list details + role ---
  useEffect(() => {
    if (!listId) return;
    (async () => {
      try {
        const listRef = doc(db, "lists", listId);
        const listSnap = await getDoc(listRef);
        if (!listSnap.exists()) {
          setErrMsg("List not found or you don’t have access.");
          return;
        }
        const data = listSnap.data() as any;
        setListName(data?.name ?? "");

        const uid = auth.currentUser?.uid ?? "";
        const roles = data?.roles ?? {};
        const userId = data?.userId;
        let r: Role | null = null;
        if (uid === userId) r = "owner";
        else if (roles?.[uid]) r = roles[uid];
        setRole(r);

        // Load items
        const itemsCol = collection(db, "lists", listId, "items");
        const qy = query(itemsCol, orderBy("value"));
        const snap = await getDocs(qy);

        setItems(
          snap.docs
            .map((d) => {
              const data = d.data() as any;
              return { id: d.id, value: data?.value ?? "" };
            })
            .filter((r) => r.value !== "")
        );
      } catch (err: any) {
        console.error("ListEditor: failed to load", err);
        setErrMsg("Unable to load list or insufficient permissions.");
      }
    })();
  }, [listId]);

  const touchUpdatedAt = async () => {
    if (!listId) return;
    try {
      await updateDoc(doc(db, "lists", listId), { updatedAt: serverTimestamp() });
    } catch {
      /* no-op */
    }
  };

  // --- CRUD actions ---
  const handleAdd = async () => {
    if (readOnly || !listId) return;
    const val = newValue.trim();
    if (!val) return;

    const itemsCol = collection(db, "lists", listId, "items");
    const ref = await addDoc(itemsCol, {
      value: val,
      userId: auth.currentUser?.uid ?? null,
      createdAt: serverTimestamp(),
    });

    setItems((prev) =>
      [...prev, { id: ref.id, value: val }].sort((a, b) =>
        a.value.localeCompare(b.value, undefined, { sensitivity: "base" })
      )
    );
    setNewValue("");
    await touchUpdatedAt();
  };

  const handleDelete = async (itemId: string) => {
    if (readOnly || !listId) return;
    await deleteDoc(doc(db, "lists", listId, "items", itemId));
    setItems((prev) => prev.filter((it) => it.id !== itemId));
    await touchUpdatedAt();
  };

  const handlePasteImport = async () => {
    if (readOnly || !listId) return;
    const incoming = Array.from(
      new Set(
        pasteText
          .split("\n")
          .map((l) => l.trim())
          .filter((l) => l.length > 0)
      )
    );
    if (incoming.length === 0) {
      setPasteOpen(false);
      setPasteText("");
      return;
    }

    const itemsCol = collection(db, "lists", listId, "items");
    const existingSnap = await getDocs(query(itemsCol, orderBy("value")));
    const existing = new Set(
      existingSnap.docs.map((d) => (d.data() as any)?.value).filter(Boolean)
    );

    const toInsert = incoming.filter((v) => !existing.has(v));
    if (toInsert.length === 0) {
      setPasteOpen(false);
      setPasteText("");
      return;
    }

    const batch = writeBatch(db);
    toInsert.forEach((val) => {
      const ref = doc(itemsCol);
      batch.set(ref, {
        value: val,
        userId: auth.currentUser?.uid ?? null,
        createdAt: serverTimestamp(),
      });
    });
    await batch.commit();

    const snap = await getDocs(query(itemsCol, orderBy("value")));
    setItems(
      snap.docs.map((d) => ({ id: d.id, value: (d.data() as any)?.value ?? "" }))
    );

    setPasteText("");
    setPasteOpen(false);
    await touchUpdatedAt();
  };

  const handleSaveName = async () => {
    if (readOnly || !listId) return;
    const trimmed = editedName.trim();
    if (!trimmed || trimmed === listName) {
      setEditingName(false);
      return;
    }
    const listRef = doc(db, "lists", listId);
    await updateDoc(listRef, { name: trimmed, updatedAt: serverTimestamp() });
    setListName(trimmed);
    setEditingName(false);
  };

  const handleEditOpen = (item: ListItem) => {
    if (readOnly) return;
    setEditItemId(item.id);
    setEditValue(item.value);
    setEditOpen(true);
  };

  const handleEditSave = async () => {
    if (readOnly || !listId || !editItemId) return;
    const trimmed = editValue.trim();
    if (!trimmed) return;

    const itemRef = doc(db, "lists", listId, "items", editItemId);
    await updateDoc(itemRef, { value: trimmed, updatedAt: serverTimestamp() });

    setItems((prev) =>
      prev
        .map((it) => (it.id === editItemId ? { ...it, value: trimmed } : it))
        .sort((a, b) =>
          a.value.localeCompare(b.value, undefined, { sensitivity: "base" })
        )
    );

    setEditOpen(false);
    setEditItemId(null);
    setEditValue("");
    await touchUpdatedAt();
  };

  const handleOpenSplit = (item: ListItem) => {
    if (readOnly) return;
    const parts = (item.value || "")
      .split(/[\/,;]+/)
      .map((p) => p.trim())
      .filter(Boolean);
    setSplitItemId(item.id);
    setSplitOriginalValue(item.value);
    setSplitParts(parts.length ? parts : [""]);
    setSplitError(null);
    setSplitOpen(true);
  };

  const handleSaveSplit = async () => {
    if (readOnly || !listId || !splitItemId) return;
    const trimmed = splitParts.map((p) => p.trim()).filter(Boolean);
    if (!trimmed.length) {
      setSplitError("Add at least one item.");
      return;
    }

    const existingLower = new Set<string>();
    items.forEach((it) => {
      if (it.id !== splitItemId) existingLower.add(it.value.toLowerCase());
    });

    const uniqueParts: string[] = [];
    trimmed.forEach((p) => {
      const lc = p.toLowerCase();
      if (!existingLower.has(lc) && !uniqueParts.some((q) => q.toLowerCase() === lc)) {
        uniqueParts.push(p);
        existingLower.add(lc);
      }
    });

    if (!uniqueParts.length) {
      setSplitError("All split items already exist in this list.");
      return;
    }

    const itemsCol = collection(db, "lists", listId, "items");
    const batch = writeBatch(db);
    batch.delete(doc(itemsCol, splitItemId));
    const newEntries: ListItem[] = [];
    uniqueParts.forEach((val) => {
      const ref = doc(itemsCol);
      batch.set(ref, {
        value: val,
        userId: auth.currentUser?.uid ?? null,
        createdAt: serverTimestamp(),
      });
      newEntries.push({ id: ref.id, value: val });
    });

    await batch.commit();

    setItems((prev) =>
      [...prev.filter((it) => it.id !== splitItemId), ...newEntries].sort((a, b) =>
        a.value.localeCompare(b.value, undefined, { sensitivity: "base" })
      )
    );

    setSplitOpen(false);
    setSplitItemId(null);
    setSplitOriginalValue("");
    setSplitParts([]);
    setSplitError(null);
    await touchUpdatedAt();
  };

  const columns: GridColDef[] = [
    { field: "value", headerName: "List Item", flex: 1 },
    {
      field: "split",
      headerName: "",
      width: 150,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        if (readOnly) return null;
        const val = String(params.row.value || "");
        const hasSeparator = /[\/,;]/.test(val);
        if (!hasSeparator) return null;
        return (
          <Button
            variant="outlined"
            size="small"
            onClick={() => handleOpenSplit({ id: String(params.id), value: val })}
          >
            Suggest Split
          </Button>
        );
      },
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 120,
      getActions: (params) => {
        if (readOnly) return [];
        return [
          <GridActionsCellItem
            key="edit"
            label="Edit"
            onClick={() =>
              handleEditOpen({ id: String(params.id), value: String(params.row.value) })
            }
            showInMenu
          />,
          <GridActionsCellItem
            key="delete"
            label="Delete"
            onClick={() => handleDelete(String(params.id))}
            showInMenu
          />,
        ];
      },
    },
  ];

  if (errMsg) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">{errMsg}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <IconButton onClick={() => navigate("/lists")}>
          <ArrowBackIosNewIcon />
        </IconButton>

        {editingName && !readOnly ? (
          <TextField
            value={editedName}
            onChange={(e) => setEditedName(e.target.value)}
            size="small"
            onBlur={handleSaveName}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSaveName();
              if (e.key === "Escape") setEditingName(false);
            }}
            sx={{ ml: 1 }}
            autoFocus
          />
        ) : (
          <Box
            sx={{ display: "flex", alignItems: "center", ml: 1 }}
            onClick={() => {
              if (!readOnly) {
                setEditedName(listName);
                setEditingName(true);
              }
            }}
            title={readOnly ? "View only" : "Click to edit list name"}
          >
            <Typography variant="h5" component="h1" sx={{ mr: 1 }}>
              {readOnly ? "View List:" : "Edit List:"} {listName || "…"}
            </Typography>
            {!readOnly && <ModeEditIcon fontSize="small" sx={{ opacity: 0.6 }} />}
          </Box>
        )}
      </Box>

      {/* Add item & paste */}
      <Paper
        elevation={1}
        sx={{
          p: 2,
          mb: 2,
          display: "flex",
          gap: 1,
          alignItems: "center",
          flexWrap: "wrap",
          opacity: readOnly ? 0.5 : 1,
          pointerEvents: readOnly ? "none" : "auto",
        }}
      >
        <TextField
          label="Add new item"
          placeholder="e.g. Apples"
          value={newValue}
          onChange={(e) => setNewValue(e.target.value)}
          size="small"
          fullWidth
          disabled={readOnly}
          onKeyDown={(e) => e.key === "Enter" && handleAdd()}
        />
        <Button variant="contained" onClick={handleAdd} disabled={readOnly || !newValue.trim()}>
          Add
        </Button>
        <Button variant="outlined" onClick={() => setPasteOpen(true)} disabled={readOnly}>
          Paste List
        </Button>
      </Paper>

      {/* Table */}
      <DataGrid
        rows={Array.isArray(items) ? items : []}
        columns={columns}
        getRowId={(row: ListItem) => row.id}
        autoHeight
        pageSizeOptions={[5, 10, 25, 50]}
        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
        localeText={{ noRowsLabel: "No items in this list" }}
        disableRowSelectionOnClick
      />

      {/* Paste dialog */}
      <Dialog open={pasteOpen} onClose={() => setPasteOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Paste List</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Paste one item per line. Empty lines and duplicates will be ignored.
          </Typography>
          <TextField
            multiline
            minRows={8}
            fullWidth
            label="Items"
            placeholder={`Item 1\nItem 2\nItem 3`}
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasteOpen(false)}>Cancel</Button>
          <Button
            onClick={handlePasteImport}
            disabled={!pasteText.trim()}
            variant="contained"
          >
            Import
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Edit Item</DialogTitle>
        <DialogContent>
          <TextField
            label="Value"
            fullWidth
            autoFocus
            margin="dense"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleEditSave()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleEditSave}
            disabled={!editValue.trim() || readOnly}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Split dialog */}
      <Dialog open={splitOpen} onClose={() => setSplitOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Split item</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Original: <strong>{splitOriginalValue}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Split into separate items. Duplicates (case-insensitive) will be skipped and the list will be sorted.
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {splitParts.map((part, idx) => (
              <Box key={idx} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <TextField
                  fullWidth
                  size="small"
                  value={part}
                  onChange={(e) => {
                    const next = [...splitParts];
                    next[idx] = e.target.value;
                    setSplitParts(next);
                    setSplitError(null);
                  }}
                  autoFocus={idx === 0}
                  onKeyDown={(e) => e.key === "Enter" && handleSaveSplit()}
                />
                <IconButton
                  aria-label="Remove"
                  size="small"
                  onClick={() => {
                    setSplitParts((prev) => prev.filter((_, i) => i !== idx));
                    setSplitError(null);
                  }}
                  disabled={splitParts.length <= 1}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
            <Button
              startIcon={<AddCircleOutlineIcon />}
              size="small"
              onClick={() => {
                setSplitParts((prev) => [...prev, ""]);
                setSplitError(null);
              }}
              sx={{ alignSelf: "flex-start" }}
            >
              Add value
            </Button>
            {splitError && (
              <Typography variant="caption" color="error">
                {splitError}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSplitOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveSplit} disabled={readOnly}>
            Save split
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
